import { Check, Copy, RefreshCw, TriangleAlert } from '@orgus/ui/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, ErrorState, IconButton, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger, formatDuration, isMac, useNow } from '@orgus/ui';
import { HubKeyNotice, NeedsCheckout, ReachabilityNotice } from './JoinReachability.tsx';
import { cancelMockJoin, commandsFor, createJoinToken } from './revoke.ts';
import type { JoinToken } from './revoke.ts';

function CopyCommand({ command, label }: { command: string; label: string }) {
  const [copied, setCopied] = useState<boolean | 'failed'>(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(t);
  }, [copied]);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
    } catch {
      setCopied('failed');
    }
  };
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-start gap-2 rounded-card border border-border bg-bg py-2 pl-3 pr-1">
        <code className="min-w-0 flex-1 select-all break-all py-1 font-mono text-ui text-fg" aria-label={label}>
          {command}
        </code>
        <IconButton label={copied === true ? 'Copied' : 'Copy command'} icon={copied === true ? <Check className="text-status-success" /> : <Copy />} size="sm" onClick={copy} />
      </div>
      {copied === 'failed' && <p className="text-caption text-status-danger">The browser blocked copying: select the command and copy it by hand.</p>}
    </div>
  );
}

type Phase = { kind: 'creating' } | { kind: 'error'; error: unknown } | { kind: 'ready'; token: JoinToken; createdAt: number };

/**
 * The command-line path, behind "Install from the command line": the hub mints a single-use join
 * token only when this opens, and shows the command that enrols the Go node agent with it (for
 * developers and automation; people download, run and approve).
 * The dialog around it notices the machine when it connects.
 */
export function DeveloperJoin() {
  const [phase, setPhase] = useState<Phase>({ kind: 'creating' });
  const [tab, setTab] = useState<'windows' | 'posix'>(isMac ? 'posix' : 'windows');
  const now = useNow(1000);
  const reqRef = useRef(0);

  const create = useCallback(async () => {
    const req = ++reqRef.current;
    setPhase({ kind: 'creating' });
    try {
      const token = await createJoinToken();
      if (req === reqRef.current) setPhase({ kind: 'ready', token, createdAt: Date.now() });
    } catch (e) {
      if (req === reqRef.current) setPhase({ kind: 'error', error: e });
    }
  }, []);

  useEffect(() => {
    void create();
    return () => {
      reqRef.current++;
      cancelMockJoin();
    };
  }, [create]);

  if (phase.kind === 'creating') {
    return (
      <div className="flex flex-col gap-3" role="status" aria-label="Making a join command">
        <Skeleton className="h-12" />
        <Skeleton className="h-16" />
      </div>
    );
  }
  if (phase.kind === 'error') return <ErrorState variant="inline" error={phase.error} onRetry={create} />;

  const cmds = commandsFor(phase.token);
  const expired = now >= phase.token.expiresAt;
  return (
    <div className="flex flex-col gap-3">
      <ReachabilityNotice token={phase.token} />
      <HubKeyNotice token={phase.token} />
      <NeedsCheckout />
      <Tabs value={tab} onValueChange={(v) => setTab(v as 'windows' | 'posix')}>
        <TabsList aria-label="Operating system of the new machine">
          <TabsTrigger value="windows">Windows</TabsTrigger>
          <TabsTrigger value="posix">macOS and Linux</TabsTrigger>
        </TabsList>
        <TabsContent value="windows" className="pt-3">
          <CopyCommand command={cmds.windows} label="Join command for Windows, in PowerShell or Command Prompt" />
        </TabsContent>
        <TabsContent value="posix" className="pt-3">
          <CopyCommand command={cmds.posix} label="Join command for macOS and Linux, in a terminal" />
        </TabsContent>
      </Tabs>
      {expired ? (
        <div role="alert" className="flex items-center gap-3 rounded-card border border-status-attention-line bg-status-attention-soft px-3 py-3 text-ui">
          <TriangleAlert className="size-4 shrink-0 text-status-attention" aria-hidden />
          <p className="min-w-0 flex-1 text-fg-muted">
            <span className="font-medium text-status-attention-ink">This command has expired.</span> Join commands work once, within 15 minutes.
          </p>
          <Button size="sm" icon={<RefreshCw />} onClick={() => void create()}>
            Make a new command
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-3 text-caption text-fg-subtle">
          <span className="num">Works once, expires in {formatDuration(Math.max(60_000, Math.floor((phase.token.expiresAt - now) / 60_000) * 60_000))}</span>
          <Button size="sm" variant="ghost" icon={<RefreshCw />} className="ml-auto" onClick={() => void create()}>
            New command
          </Button>
        </div>
      )}
    </div>
  );
}
